
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/api';
import { useUser } from '../context/UserContext';
import { notifyManagerReviewReady } from '../lib/notifications';

const B = {
  black: '#1e1e23', surface: '#26262d', surfaceLight: '#2e2e36',
  border: '#1e1a2e', borderLight: '#2e2850',
  purple: '#7B5CC8', purpleDim: '#4a3880', purpleDeep: '#1a1030',
  gold: '#C9A84C', goldDim: '#8a6e2a',
  textPrimary: '#ffffff', textSecondary: '#8888aa', textMuted: '#444466',
  green: '#1D9E75',
};

const priorities = [
  { key: 'must', label: 'Must do', bg: '#0f0810', border: '#4a1530', tag: '#c0392b', desc: 'Critical this quarter' },
  { key: 'should', label: 'Should do', bg: '#0f0e08', border: '#4a3a10', tag: '#C9A84C', desc: 'Important, not urgent' },
  { key: 'could', label: 'Could do', bg: '#080f0e', border: '#0e3a2a', tag: '#1D9E75', desc: 'Nice to have' },
];

const TABS = ['Self-evaluation', 'Priorities', 'Review & submit'];
const EMPTY = { achievements: '', skills: '', must: '', should: '', could: '' };

const input = { width: '100%', boxSizing: 'border-box', fontSize: 13, resize: 'vertical', padding: '10px 12px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: B.surfaceLight, color: B.textPrimary, fontFamily: 'inherit', lineHeight: 1.6 };

export default function EmployeeSelfEval() {
  const { employee } = useUser();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [cycle, setCycle] = useState(null);
  const [review, setReview] = useState(null);
  const [reviewerName, setReviewerName] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saveMsg, setSaveMsg] = useState('');
  const [loading, setLoading] = useState(true);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const progress = Math.round((Object.values(form).filter(v => v.trim().length > 10).length / 5) * 100);
  const hasContent = Object.values(form).some(v => v.trim().length > 0);

  useEffect(() => {
    fetchCycleAndReview();
  }, [employee]);

  const fetchCycleAndReview = async () => {
    if (!employee) return;
    setLoading(true);

    // Get open Annual cycle
    const { data: cycleData } = await supabase
      .from('review_cycles')
      .select('*')
      .eq('is_open', true)
      .eq('type', 'Annual')
      .single();

    if (!cycleData) { setLoading(false); return; }
    setCycle(cycleData);

    // Get or create review record
    const { data: existing } = await supabase
      .from('annual_reviews')
      .select('*')
      .eq('employee_id', employee.id)
      .eq('cycle_id', cycleData.id)
      .single();

    if (existing) {
      setReview(existing);
      setForm({
        achievements: existing.achievements || '',
        skills: existing.skills || '',
        must: existing.must_do || '',
        should: existing.should_do || '',
        could: existing.could_do || '',
      });
      if (existing.emp_status === 'submitted') setSubmitted(true);
    }

    // If HR set a reviewer override that differs from the Okta manager,
    // look up the reviewer's display name so the submit-confirm copy can
    // accurately say "Your reviewer [Name]" instead of falsely showing
    // the org-chart manager_name. RLS on employees allows the reviewer
    // to read this employee's row, but not necessarily vice versa — so
    // we fall back to the email if the lookup returns nothing.
    const reviewerEmail = employee.review_manager_email;
    if (reviewerEmail && reviewerEmail.toLowerCase() !== (employee.manager_email || '').toLowerCase()) {
      const { data: reviewer } = await supabase
        .from('employees').select('name').eq('email', reviewerEmail).maybeSingle();
      setReviewerName(reviewer?.name || null);
    }

    setLoading(false);
  };

  const flash = msg => { setSaveMsg(msg); setTimeout(() => setSaveMsg(''), 2500); };

  const saveDraft = async () => {
    if (!employee || !cycle || !hasContent) return;
    setSaving(true);
    const payload = { employee_id: employee.id, cycle_id: cycle.id, achievements: form.achievements, skills: form.skills, must_do: form.must, should_do: form.should, could_do: form.could, emp_status: 'in_progress', updated_at: new Date().toISOString() };

    const { error } = review
      ? await supabase.from('annual_reviews').update(payload).eq('id', review.id)
      : await supabase.from('annual_reviews').insert(payload).select().single();

    if (!error) {
      const now = new Date();
      setSavedAt(`${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`);
      flash('Draft saved');
    }
    setSaving(false);
  };

  const submitReview = async () => {
    if (!employee || !cycle) return;
    setSaving(true);
    const payload = { employee_id: employee.id, cycle_id: cycle.id, achievements: form.achievements, skills: form.skills, must_do: form.must, should_do: form.should, could_do: form.could, emp_status: 'submitted', submitted_at: new Date().toISOString(), updated_at: new Date().toISOString() };

    const { error } = review
      ? await supabase.from('annual_reviews').update(payload).eq('id', review.id)
      : await supabase.from('annual_reviews').insert(payload);

    if (!error) {
      // Notify the effective reviewer — review_manager_email override wins,
      // else fall back to the Okta manager_email.
      const reviewer = employee.review_manager_email || employee.manager_email;
      if (reviewer) {
        await notifyManagerReviewReady({ managerEmail: reviewer, employeeName: employee.name, cycleLabel: cycle.label });
      }
      setSubmitted(true);
    }
    setSaving(false);
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: B.black, color: B.textSecondary, fontSize: 12, letterSpacing: '0.08em' }}>LOADING...</div>;

  if (!cycle) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: B.black, fontFamily: 'inherit' }}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: 14, color: B.textSecondary, marginBottom: 16 }}>No annual review cycle is currently open.</p>
        <button onClick={() => navigate('/')} style={{ fontSize: 12, letterSpacing: '0.06em', padding: '8px 20px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: B.textSecondary, cursor: 'pointer' }}>← BACK TO PORTAL</button>
      </div>
    </div>
  );

  const Header = () => (
    <div style={{ background: B.black, borderBottom: `0.5px solid ${B.border}`, padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>

          <img src="/logo-full.png" alt="xLight" style={{ height: 22, width: 'auto', display: 'block' }} />

        </button>
        <span style={{ width: 1, height: 16, background: B.border, margin: '0 8px' }} />
        <span style={{ fontSize: 11, color: B.textSecondary, letterSpacing: '0.08em', textTransform: 'uppercase' }}>People</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {savedAt && <span style={{ fontSize: 11, color: B.textMuted }}>Draft saved at {savedAt}</span>}
        {saveMsg && <span style={{ fontSize: 11, color: B.green }}>{saveMsg}</span>}
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: 11, color: B.textSecondary, margin: 0, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{cycle?.label}</p>
          <p style={{ fontSize: 12, color: B.textPrimary, margin: '2px 0 0' }}>{employee?.name} · {employee?.department}</p>
        </div>
      </div>
    </div>
  );

  // Effective reviewer for the submit-confirm copy. When HR has set a
  // review_manager_email override that differs from the Okta manager, show
  // that person ("Your reviewer …"); otherwise fall back to the standard
  // "Your manager …" copy. The reviewer's name comes from the employees
  // lookup we did in fetch; fall back to the email if the name lookup
  // returned nothing.
  const overrideReviewer = !!employee?.review_manager_email
    && (employee.review_manager_email || '').toLowerCase() !== (employee?.manager_email || '').toLowerCase();
  const reviewerLabel = overrideReviewer ? 'Your reviewer' : 'Your manager';
  const reviewerDisplayName = overrideReviewer
    ? (reviewerName || employee.review_manager_email)
    : (employee?.manager_name || 'Your manager');

  if (submitted) return (
    <div style={{ background: B.black, minHeight: '100vh', fontFamily: 'inherit' }}>
      <Header />
      <div style={{ padding: '3rem 2rem', textAlign: 'center', maxWidth: 460, margin: '0 auto' }}>
        <div style={{ width: 52, height: 52, borderRadius: '50%', background: B.purpleDeep, border: `1px solid ${B.purple}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', fontSize: 20, color: B.purple }}>✓</div>
        <h2 style={{ fontSize: 18, fontWeight: 400, color: B.textPrimary, marginBottom: 10 }}>Evaluation submitted</h2>
        <p style={{ fontSize: 13, color: B.textSecondary, lineHeight: 1.7, marginBottom: 24 }}>{reviewerLabel} has been notified and will complete the review shortly. You will receive an email when feedback is ready.</p>
        <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '1rem 1.25rem', textAlign: 'left', marginBottom: 20 }}>
          <p style={{ fontSize: 11, color: B.textSecondary, marginBottom: 4, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Routed to</p>
          <p style={{ fontSize: 13, color: B.textPrimary }}>{reviewerDisplayName}</p>
        </div>
        <button onClick={() => navigate('/')} style={{ fontSize: 12, letterSpacing: '0.06em', padding: '9px 24px', borderRadius: 6, border: `0.5px solid ${B.borderLight}`, background: 'transparent', color: B.textPrimary, cursor: 'pointer' }}>← BACK TO PORTAL</button>
      </div>
    </div>
  );

  const SaveDraftBtn = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <button onClick={saveDraft} disabled={!hasContent || saving}
        style={{ fontSize: 12, letterSpacing: '0.06em', padding: '9px 20px', borderRadius: 6, border: `0.5px solid ${B.borderLight}`, background: 'transparent', color: hasContent ? B.textSecondary : B.textMuted, cursor: hasContent ? 'pointer' : 'not-allowed' }}>
        {saving ? 'SAVING...' : 'SAVE DRAFT'}
      </button>
      {saveMsg && <span style={{ fontSize: 11, color: B.green }}>{saveMsg}</span>}
    </div>
  );

  return (
    <div style={{ background: B.black, minHeight: '100vh', fontFamily: 'inherit' }}>
      <Header />
      <div style={{ background: B.surface, borderBottom: `0.5px solid ${B.border}`, padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex' }}>
          {TABS.map((t, i) => (
            <button key={t} onClick={() => setTab(i)}
              style={{ fontSize: 12, letterSpacing: '0.05em', color: tab === i ? B.textPrimary : B.textSecondary, background: 'none', border: 'none', borderBottom: tab === i ? `1.5px solid ${B.purple}` : '1.5px solid transparent', padding: '10px 14px', cursor: 'pointer' }}>
              {i < tab ? '✓ ' : ''}{t.toUpperCase()}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 64, height: 2, background: B.border, borderRadius: 99 }}>
            <div style={{ width: `${progress}%`, height: '100%', background: B.purple, borderRadius: 99 }} />
          </div>
          <span style={{ fontSize: 11, color: B.textSecondary }}>{progress}%</span>
        </div>
      </div>

      <div style={{ maxWidth: 660, margin: '0 auto', padding: '2rem 1.5rem' }}>

        {tab === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
            <p style={{ fontSize: 13, color: B.textSecondary, lineHeight: 1.7 }}>Reflect on the past year.</p>
            {[{ k: 'achievements', label: 'Key achievements & learnings', hint: 'What are you most proud of? What was challenging and what did you learn from it?' }, { k: 'skills', label: 'Skills & development', hint: 'What skills have you grown? Where do you want to develop further?' }].map(({ k, label, hint }) => (
              <div key={k}>
                <label style={{ fontSize: 12, letterSpacing: '0.07em', textTransform: 'uppercase', color: B.textSecondary, display: 'block', marginBottom: 6 }}>{label}</label>
                <p style={{ fontSize: 12, color: B.textMuted, marginBottom: 8, lineHeight: 1.5 }}>{hint}</p>
                <textarea value={form[k]} onChange={e => set(k, e.target.value)} rows={4} placeholder="Write your response here..." style={input} />
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <SaveDraftBtn />
              <button onClick={() => setTab(1)} style={{ fontSize: 12, letterSpacing: '0.06em', padding: '9px 22px', borderRadius: 6, border: `0.5px solid ${B.borderLight}`, background: 'transparent', color: B.textPrimary, cursor: 'pointer' }}>NEXT →</button>
            </div>
          </div>
        )}

        {tab === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ fontSize: 13, color: B.textSecondary, lineHeight: 1.7 }}>Plan your next 3 months using the MoSCoW framework.</p>
            {priorities.map(({ key, label, bg, border, tag, desc }) => (
              <div key={key} style={{ background: bg, border: `0.5px solid ${border}`, borderRadius: 8, padding: '1rem 1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 10, padding: '2px 10px', borderRadius: 99, background: tag, color: '#fff', letterSpacing: '0.06em' }}>{label.toUpperCase()}</span>
                  <span style={{ fontSize: 11, color: B.textSecondary }}>{desc}</span>
                </div>
                <textarea value={form[key]} onChange={e => set(key, e.target.value)} rows={3} placeholder={`List your ${label.toLowerCase()} priorities...`} style={{ ...input, background: 'rgba(255,255,255,0.03)', border: `0.5px solid ${border}` }} />
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button onClick={() => setTab(0)} style={{ fontSize: 12, letterSpacing: '0.06em', padding: '9px 22px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'none', color: B.textSecondary, cursor: 'pointer' }}>← BACK</button>
                <SaveDraftBtn />
              </div>
              <button onClick={() => setTab(2)} style={{ fontSize: 12, letterSpacing: '0.06em', padding: '9px 22px', borderRadius: 6, border: `0.5px solid ${B.borderLight}`, background: 'transparent', color: B.textPrimary, cursor: 'pointer' }}>NEXT →</button>
            </div>
          </div>
        )}

        {tab === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ fontSize: 13, color: B.textSecondary, lineHeight: 1.7 }}>Review your responses before submitting. Once submitted {reviewerLabel.toLowerCase()} will be notified.</p>
            {[{ label: 'Key achievements & learnings', val: form.achievements }, { label: 'Skills & development', val: form.skills }].map(({ label, val }) => (
              <div key={label} style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '12px 16px' }}>
                <p style={{ fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: B.textSecondary, marginBottom: 6 }}>{label}</p>
                <p style={{ fontSize: 13, color: val ? B.textPrimary : B.textMuted, lineHeight: 1.7, fontStyle: val ? 'normal' : 'italic' }}>{val || 'Not filled in'}</p>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10 }}>
              {priorities.map(({ key, label, bg, border, tag }) => (
                <div key={key} style={{ flex: 1, background: bg, border: `0.5px solid ${border}`, borderRadius: 8, padding: '10px 12px' }}>
                  <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 99, background: tag, color: '#fff', display: 'inline-block', marginBottom: 6 }}>{label.toUpperCase()}</span>
                  <p style={{ fontSize: 12, color: form[key] ? B.textPrimary : B.textMuted, lineHeight: 1.5, fontStyle: form[key] ? 'normal' : 'italic' }}>{form[key] || 'Empty'}</p>
                </div>
              ))}
            </div>
            <div style={{ background: B.purpleDeep, border: `0.5px solid ${B.purpleDim}`, borderRadius: 8, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: B.purple, flexShrink: 0 }} />
              <p style={{ fontSize: 12, color: B.textSecondary, margin: 0 }}>On submit, <span style={{ color: B.textPrimary }}>{reviewerDisplayName}</span> will be notified to complete the review.</p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button onClick={() => setTab(1)} style={{ fontSize: 12, letterSpacing: '0.06em', padding: '9px 22px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'none', color: B.textSecondary, cursor: 'pointer' }}>← BACK</button>
                <SaveDraftBtn />
              </div>
              <button onClick={submitReview} disabled={saving}
                style={{ fontSize: 12, letterSpacing: '0.08em', padding: '10px 28px', borderRadius: 6, border: 'none', background: B.purple, color: '#fff', cursor: 'pointer' }}>
                {saving ? 'SUBMITTING...' : 'SUBMIT EVALUATION'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
